import Image from "next/image";
import { useRouter } from "next/router";
import { memo, useCallback, useMemo } from "react";
import { useRecoilState } from "recoil";
import { BorderButton } from "src/components/elements/button/BorderButton";
import { RadioButton } from "src/components/elements/button/RadioButton";
import { Text } from "src/components/elements/typography/Text";
import { CreditCard } from "src/modules/domain/order/payment/creditCard";
import {
  CreditCardBrandLogoSize,
  CreditCardType,
} from "src/modules/domain/order/payment/creditCard/cardBrand";
import { PAYMENT_TYPE } from "src/modules/domain/order/payment/paymentType/paymentType";
import { selectedPaymentMethodState } from "..";
import styles from "./styles.module.scss";

type Props = {
  type: CreditCard["card_company"];
  number: CreditCard["card_number"];
  id: CreditCard["card_id"];
  onSelectCard: (id: CreditCard["card_id"]) => void;
};

export const CreditCardButton = memo(
  ({ type, number, id, onSelectCard }: Props) => {
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useRecoilState(
      selectedPaymentMethodState
    );
    const router = useRouter();
    const cardBandImage = useMemo(
      () => CreditCardType.getCreditCardBrandLogo(type),
      [type]
    );
    const cardBrandImageAlt = useMemo(
      () => CreditCardType.getTextByCreditCardType(type),
      [type]
    );
    const { width, height } = useMemo(
      () => CreditCardBrandLogoSize[type],
      [type]
    );
    const redirectSearchParam = router.asPath;
    const handlePressEditButton = useCallback(() => {
      router.push({
        pathname: "/mypage/payment",
        query: {
          redirect_url: redirectSearchParam,
        },
      });
    }, [router, redirectSearchParam]);

    const handleSelectCreditCard = useCallback(() => {
      onSelectCard(id);
      setSelectedPaymentMethod(PAYMENT_TYPE.CREDIT);
    }, [id, onSelectCard, setSelectedPaymentMethod]);

    const isButtonSelected = useMemo(
      () => selectedPaymentMethod === PAYMENT_TYPE.CREDIT,
      [selectedPaymentMethod]
    );

    return (
      <div className={styles.creditCardButtonContainer}>
        <RadioButton
          isActive={isButtonSelected}
          onPress={handleSelectCreditCard}
        >
          <div className={styles.cardInfo}>
            {cardBandImage && (
              <Image
                src={cardBandImage}
                alt={cardBrandImageAlt}
                width={width}
                height={height}
                className={styles.logoImage}
              />
            )}
            <Text size="size-16" weight="500">
              {number}
            </Text>
          </div>
        </RadioButton>
        <BorderButton
          className={styles.editButton}
          onClick={handlePressEditButton}
        >
          カードを変更
        </BorderButton>
      </div>
    );
  }
);
